import {
  type ResolvedSound,
  SOUND_FILE_ACCEPT,
  SOUND_GAIN_DB_MAX,
  SOUND_GAIN_DB_MIN,
  type SoundEventInfo,
  type SoundSetting,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Slider } from "@loot-os/ui/components/slider";
import { toast } from "@loot-os/ui/components/sonner";
import { Spinner } from "@loot-os/ui/components/spinner";
import { TRPCClientError } from "@trpc/client";
import { Link2, Play, Square, Upload, Volume1, Volume2, VolumeX } from "lucide-react";
import { type DragEvent, useEffect, useRef, useState } from "react";

import { playSoundPreview, stopSoundPreview, useSoundPreviewPlaying } from "./soundPreview";
import { type SoundRowState, soundRowState } from "./soundRowState";

/** How long the trim waits after an arrow key before it saves, so a run of presses saves once. */
const KEY_COMMIT_DELAY_MS = 600;

/** The label for an event that plays through its fallback, e.g. `Same as "Box opening: Big win"`. */
function viaText(via: ResolvedSound["via"], events: readonly SoundEventInfo[]): string {
  const event = events.find((e) => e.key === via);
  return event ? `Same as "${event.group}: ${event.label}"` : "Same as another event";
}

/**
 * On a row playing a sound its scope does not own: a trim is saved as the scope's own setting
 * (the file and the trim), so the row stops following the default or event it inherited from.
 */
function inheritedTrimNote(
  scope: "platform" | "tenant",
  via: ResolvedSound["via"],
  events: readonly SoundEventInfo[],
): string {
  const event = via && events.find((e) => e.key === via);
  // Without a fallback the sound came from the platform, since the platform page owns its others.
  const source = event ? `"${event.group}: ${event.label}"` : "the platform default";
  const owner = scope === "tenant" ? "the store's" : "the platform's";
  return `Changing the loudness makes this sound ${owner} own copy, which stops following ${source}.`;
}

function gainText(gainDb: number): string {
  return gainDb === 0 ? "Original" : `${gainDb > 0 ? "+" : ""}${gainDb} dB`;
}

/**
 * Reports a failed upload the mutation cache has not: a refused call already showed its toast
 * (`lib/queryClient.ts`), but the file checks and the PUT fail before any call is made.
 */
function toastUploadFailure(error: unknown) {
  if (error instanceof TRPCClientError) return;
  toast.error(error instanceof Error ? error.message : "the file could not be uploaded");
}

/** What the row plays now, as a badge: its own file stands out, borrowed and off ones recede. */
function StatusBadge({ state, label }: { state: SoundRowState; label: string }) {
  switch (state.label) {
    case "Custom":
      return <Badge>{label}</Badge>;
    case "Platform default":
      return <Badge variant="outline">{label}</Badge>;
    case "Same as …":
      return (
        <Badge variant="outline" className="max-w-full text-muted-foreground">
          <Link2 data-icon="inline-start" />
          <span className="truncate">{label}</span>
        </Badge>
      );
    case "Turned off":
      return (
        <Badge variant="secondary">
          <VolumeX data-icon="inline-start" />
          {label}
        </Badge>
      );
    case "Silent":
      return (
        <Badge variant="outline" className="border-dashed text-muted-foreground">
          {label}
        </Badge>
      );
  }
}

export function SoundRow({
  event,
  events,
  scope,
  resolved,
  own,
  canEdit,
  onUpload,
  onSet,
}: {
  event: SoundEventInfo;
  events: readonly SoundEventInfo[];
  scope: "platform" | "tenant";
  resolved: ResolvedSound | undefined;
  own: SoundSetting | undefined;
  canEdit: boolean;
  onUpload: (file: File) => void | Promise<void>;
  onSet: (setting: SoundSetting | null) => void | Promise<void>;
}) {
  const state = soundRowState(resolved, own, scope);
  const fileInput = useRef<HTMLInputElement>(null);
  // The trim while the thumb is held, or while it waits to save; null shows the saved value.
  const [dragging, setDragging] = useState<number | null>(null);
  // This row's own save in flight: the other rows stay usable.
  const [pending, setPending] = useState<"upload" | "set" | null>(null);
  const [dropping, setDropping] = useState(false);
  const keyCommit = useRef<{ timer: number; value: number } | null>(null);
  // A save can land after the row is gone (the page closed): it keeps the save, plays nothing.
  const mounted = useRef(false);
  const playing = useSoundPreviewPlaying(event.key);
  const gainDb = dragging ?? state.gainDb;
  const busy = pending !== null || !canEdit;
  // Where 0 dB sits along the track.
  const originPercent = ((0 - SOUND_GAIN_DB_MIN) / (SOUND_GAIN_DB_MAX - SOUND_GAIN_DB_MIN)) * 100;
  const ownHasFile = own !== undefined && "url" in own;
  const label = state.label === "Same as …" ? viaText(resolved?.via, events) : state.label;

  // Failures reach the user as a toast from the mutation cache, so a rejection is dropped here.
  const run = async (kind: "upload" | "set", action: () => void | Promise<void>) => {
    setPending(kind);
    try {
      await action();
    } finally {
      setPending(null);
    }
  };
  const save = (setting: SoundSetting | null) =>
    void run("set", () => onSet(setting)).catch(() => {});
  const upload = (file: File) => void run("upload", () => onUpload(file)).catch(toastUploadFailure);

  const commit = async (value: number) => {
    if (!state.url || value === state.gainDb) {
      setDragging(null);
      return;
    }
    const url = state.url;
    try {
      // Zero is the original, so it is stored as no trim at all.
      await onSet(value === 0 ? { url } : { url, gainDb: value });
      if (mounted.current) playSoundPreview(event.key, url, value);
    } finally {
      // Held until the save lands, so the thumb never jumps back to the old trim on the way.
      setDragging(null);
    }
  };
  const commitNow = (value: number) => void commit(value).catch(() => {});
  const flushKeyCommit = () => {
    const waiting = keyCommit.current;
    if (!waiting) return;
    window.clearTimeout(waiting.timer);
    keyCommit.current = null;
    commitNow(waiting.value);
  };
  // A trim still waiting when the page closes is saved, and the row's preview stops.
  const flushOnUnmount = useRef(flushKeyCommit);
  flushOnUnmount.current = flushKeyCommit;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      flushOnUnmount.current();
      stopSoundPreview(event.key);
    };
  }, [event.key]);

  const acceptsDrop = (e: DragEvent) => !busy && e.dataTransfer.types.includes("Files");

  return (
    // A group of the event's controls, and a drop target for its file.
    <fieldset
      aria-label={`${event.label} sound`}
      className="relative grid min-w-0 gap-3 py-4 sm:grid-cols-[1fr_auto] sm:items-center"
      onDragOver={(e) => {
        if (!acceptsDrop(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setDropping(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropping(false);
      }}
      onDrop={(e) => {
        if (!acceptsDrop(e)) return;
        e.preventDefault();
        setDropping(false);
        const file = e.dataTransfer.files[0];
        if (file) upload(file);
      }}
    >
      {dropping && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -inset-x-2 inset-y-1 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-background/90 text-sm font-medium text-primary"
        >
          Drop an MP3 to use it for {event.label}
        </div>
      )}
      <div className="grid min-h-12 min-w-0 content-center gap-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-medium">{event.label}</span>
          <StatusBadge state={state} label={label} />
        </div>
        <p className="text-xs text-muted-foreground">{event.description}</p>
      </div>
      <div className="grid grid-cols-2 gap-2 min-[480px]:grid-cols-4 sm:flex sm:flex-wrap sm:items-center">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!state.url}
          aria-label={playing ? `Stop ${event.label}` : `Play ${event.label}`}
          onClick={() => {
            if (playing) stopSoundPreview(event.key);
            else if (state.url) playSoundPreview(event.key, state.url, gainDb);
          }}
        >
          {playing ? <Square data-icon="inline-start" /> : <Play data-icon="inline-start" />}
          {playing ? "Stop" : "Play"}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept={SOUND_FILE_ACCEPT}
          className="hidden"
          aria-label={`${event.label} sound file`}
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) upload(file);
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => fileInput.current?.click()}
        >
          {pending === "upload" ? (
            <Spinner data-icon="inline-start" aria-label="Uploading" />
          ) : (
            <Upload data-icon="inline-start" />
          )}
          {ownHasFile ? "Replace" : "Upload"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || state.label === "Turned off"}
          onClick={() => save({ off: true })}
        >
          Turn off
        </Button>
        {/* Removes this scope's own setting: the tenant's falls back to the platform default,
            the platform's to silence (or, for a battle event, its opener twin). */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy || !state.canRevert}
          onClick={() => save(null)}
        >
          {scope === "tenant" ? "Revert" : "Clear"}
        </Button>
      </div>
      <div className="grid gap-1 pb-3 sm:col-span-2">
        <div className="flex items-center gap-3">
          <span className="hidden w-14 text-xs text-muted-foreground sm:inline">Quieter</span>
          <Volume1 aria-hidden="true" className="size-4 shrink-0 text-muted-foreground sm:hidden" />
          <div className="relative w-full">
            {/* A run of arrow presses saves once, when the keys go quiet or focus leaves. */}
            <Slider
              onBlur={flushKeyCommit}
              min={SOUND_GAIN_DB_MIN}
              max={SOUND_GAIN_DB_MAX}
              step={1}
              value={gainDb}
              // Never disabled by its own save: a disabled thumb would drop the keyboard's focus.
              disabled={!canEdit || !state.url}
              aria-label={`${event.label} volume trim`}
              getAriaValueText={(_formatted, value) => gainText(value)}
              onValueChange={setDragging}
              onValueCommitted={(value, reason) => {
                if (keyCommit.current) window.clearTimeout(keyCommit.current.timer);
                keyCommit.current = null;
                if (reason !== "keyboard") return commitNow(value);
                keyCommit.current = {
                  value,
                  timer: window.setTimeout(() => {
                    keyCommit.current = null;
                    commitNow(value);
                  }, KEY_COMMIT_DELAY_MS),
                };
              }}
            />
            {/* The original level, where the file plays untrimmed. The label sits under the track. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 flex -translate-x-1/2 flex-col items-center"
              style={{ left: `${originPercent}%` }}
            >
              <span className="h-3 w-px -translate-y-1/2 bg-muted-foreground/60" />
              <span className="-mt-1 text-[10px] text-muted-foreground">Original</span>
            </div>
          </div>
          <span className="hidden w-14 text-right text-xs text-muted-foreground sm:inline">
            Louder
          </span>
          <Volume2 aria-hidden="true" className="size-4 shrink-0 text-muted-foreground sm:hidden" />
          <span className="w-16 shrink-0 text-right text-xs whitespace-nowrap tabular-nums">
            {gainText(gainDb)}
          </span>
        </div>
        {canEdit && own === undefined && state.url && (
          <p className="pt-3 text-xs text-muted-foreground">
            {inheritedTrimNote(scope, resolved?.via, events)}
          </p>
        )}
      </div>
    </fieldset>
  );
}
